import React, { useState, useEffect } from "react";
import { Box, Stack, Divider, Switch, FormControlLabel } from "@mui/material";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";

// Custom components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import VerifiedStatus from "components/Custom/VerifiedStatus";
import SimpleBackdrop from "components/MDLoader_Backdrop";
import { useAuth } from "context";
import { saveNotificationPreferencesAPI } from "../_request";

const IOSSwitch = styled((props) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transitionDuration: "300ms",
    "&.Mui-checked": {
      transform: "translateX(16px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: `${theme.palette.success.main} !important`,
        opacity: "1 !important",
        border: "none !important",
      },
      "&.Mui-disabled + .MuiSwitch-track": {
        opacity: "0.5 !important",
      },
      "& .MuiSwitch-thumb": {
        border: "none !important",
      },
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      color: "#33cf4d",
      border: "6px solid #fff",
    },
    "&.Mui-disabled .MuiSwitch-thumb": {
      color: theme.palette.mode === "light" ? theme.palette.grey[100] : theme.palette.grey[600],
    },
    "&.Mui-disabled + .MuiSwitch-track": {
      opacity: `${theme.palette.mode === "light" ? 0.7 : 0.3} !important`,
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 22,
    height: 22,
    border: "none !important",
    boxShadow: "0 2px 4px 0 rgb(0 35 11 / 20%)",
  },
  "& .MuiSwitch-track": {
    borderRadius: 26 / 2,
    backgroundColor: `${theme.palette.mode === "light" ? "#E9E9EA" : "#39393D"} !important`,
    opacity: "1 !important",
    transition: theme.transitions.create(["background-color"], {
      duration: 500,
    }),
    width: "100% !important",
    height: "100% !important",
    border: "none !important",
  },
}));

const NotificationPreferences = ({ customer, customerDataLoader, onChangeTab }) => {
  const { showSnackbarWithMessage } = useAuth();

  const preferencesFromBackend = customer?.notification_preferences || {};
  const customerEmail = customer?.email || "No email added";
  const customerPhone = customer?.phoneNumber ? `+1-${customer?.phoneNumber}` : "";

  const [preferences, setPreferences] = useState(preferencesFromBackend);
  const [initialPreferences, setInitialPreferences] = useState(preferencesFromBackend);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (customer?.notification_preferences) {
      setPreferences(customer.notification_preferences);
      setInitialPreferences(customer.notification_preferences);
    }
  }, [customer?.notification_preferences]);

  useEffect(() => {
    const changed = JSON.stringify(preferences) !== JSON.stringify(initialPreferences);
    setHasChanges(changed);
  }, [preferences, initialPreferences]);

  const handleToggle = (event) => {
    const { name, checked } = event.target;
    const [channel, type] = name.split("_");

    setPreferences((prev) => ({
      ...prev,
      [channel]: {
        ...prev[channel],
        [type]: checked,
      },
    }));
  };

  const handleSave = async () => {
    if (!hasChanges || isSaving) return;
    setIsSaving(true);
    try {
      const { data } = await saveNotificationPreferencesAPI({
        preferences,
        consent_source: "APP_SETTING",
      });
      if (data.success) {
        setInitialPreferences(data.data.preferences);
        setHasChanges(false);
        showSnackbarWithMessage({ type: "success", message: data.message });
      } else {
        showSnackbarWithMessage({
          type: "error",
          message: data.message || "Failed to save preferences.",
        });
      }
    } catch (error) {
      showSnackbarWithMessage({
        type: "error",
        message: error.message || "An unexpected error occurred.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setPreferences(initialPreferences);
    setHasChanges(false);
  };

  if (customerDataLoader || !customer?.notification_preferences) return <SimpleBackdrop />;

  const channelOrder = ["email", "sms"];
  const visiblePreferences = channelOrder.flatMap((channel) => {
    const types = preferences[channel] || {};
    return Object.entries(types)
      .filter(([type]) => type !== "transactional")
      .map(([type, enabled]) => ({
        key: `${channel}_${type}`,
        channel,
        type,
        enabled,
      }));
  });

  return (
    <MDBox
      sx={{
        mt: { xs: 2, md: 4 },
        border: "1px solid #E0E0E0",
        borderRadius: "8px",
        p: { xs: 2, md: 4 },
        width: "100%",
        bgcolor: "background.paper",
      }}
    >
      <MDTypography variant="h6" fontWeight="medium" mb={2}>
        Marketing Preferences
      </MDTypography>

      <Stack spacing={0} sx={{ maxHeight: { xs: "auto", md: "auto" }, overflowY: "auto" }}>
        {visiblePreferences.map(({ key, channel, type, enabled }, index) => {
          const label =
            channel === "email"
              ? `Enable email notifications`
              : channel === "sms"
              ? `Enable phone notifications`
              : `Enable ${channel} notifications`;

          const subLabel =
            channel === "email" ? customerEmail : channel === "sms" ? customerPhone : "";

          return (
            <React.Fragment key={key}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "flex-start", sm: "center" },
                  justifyContent: "space-between",
                  width: "100%",
                  gap: { xs: 1, sm: 0 },
                  py: 1,
                }}
              >
                <FormControlLabel
                  control={<IOSSwitch checked={enabled} onChange={handleToggle} name={key} />}
                  sx={{ m: 0 }}
                />

                <MDBox sx={{ ml: { xs: 0, sm: 2 }, flex: 1 }}>
                  <MDTypography
                    sx={{
                      fontWeight: 500,
                      fontSize: { xs: "0.875rem", md: "1rem" },
                      lineHeight: "24px",
                      mb: 0,
                    }}
                  >
                    {label}
                  </MDTypography>
                  {subLabel && (
                    <MDTypography
                      variant="caption"
                      sx={{ wordBreak: "break-word", lineHeight: 1.25, mt: 0 }}
                    >
                      {subLabel}
                    </MDTypography>
                  )}

                  {channel === "sms" && (
                    <>
                      {!customerPhone && (
                        <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
                          <MDTypography variant="caption" color="text.secondary">
                            No phone number added
                          </MDTypography>
                          <MDButton
                            variant="text"
                            size="small"
                            color="info"
                            onClick={() => onChangeTab(0)}
                            sx={{
                              minHeight: "unset",
                              py: 0.5,
                              px: 1,
                              fontSize: "0.75rem",
                              lineHeight: 1,
                            }}
                          >
                            + Add Phone number
                          </MDButton>
                        </Box>
                      )}

                      {customerPhone && customerPhone.trim() !== "" && (
                        <VerifiedStatus
                          status={customer.phoneVerifiedAt}
                          onClick={!customer.phoneVerifiedAt ? () => onChangeTab(0) : undefined}
                          sx={{
                            position: "static",
                            mx: 2,
                            verticalAlign: "middle",
                            cursor: !customer.phoneVerifiedAt ? "pointer" : "default",
                            "&:hover": !customer.phoneVerifiedAt ? { opacity: 0.8 } : {},
                          }}
                        />
                      )}
                    </>
                  )}
                </MDBox>
              </Box>

              {index !== visiblePreferences.length - 1 && <Divider sx={{ my: 0 }} />}
            </React.Fragment>
          );
        })}
      </Stack>

      {/* Buttons */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column-reverse", sm: "row" },
          justifyContent: { xs: "flex-end", sm: "flex-end" },
          gap: 1,
          mt: 4,
          pt: 2,
          borderTop: "1px solid #E0E0E0",
        }}
      >
        <MDButton
          variant="outlined"
          color="secondary"
          size="small"
          onClick={handleCancel}
          disabled={!hasChanges || isSaving}
        >
          Cancel
        </MDButton>
        <MDButton
          variant="contained"
          color="success"
          size="small"
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
        >
          {isSaving ? "Saving..." : "Save changes"}
        </MDButton>
      </Box>

      {isSaving && <SimpleBackdrop />}
    </MDBox>
  );
};

NotificationPreferences.propTypes = {
  customer: PropTypes.shape({
    email: PropTypes.string,
    phoneNumber: PropTypes.string,
    phoneVerifiedAt: PropTypes.string,
    notification_preferences: PropTypes.object,
  }),
  customerDataLoader: PropTypes.bool.isRequired,
  onChangeTab: PropTypes.func,
};

export default NotificationPreferences;
